/**
 * useSiteVicinity.ts — fetch the vicinity payload for a searched site, for the stats panel (T-512).
 *
 * The map's SiteVicinityLayer already fetches GET /api/vicinity to DRAW the blocks; this hook makes
 * the same payload available to the READOUT in the stats panel. They are deliberately separate:
 *   - the layer fetches only when its toggle is visible; the readout must work regardless of the
 *     map toggle, so it cannot piggy-back on the layer's fetch;
 *   - this hook runs only when the drill-down (where the readout lives) is actually open, so the
 *     extra request fires on drill-in, not on every search — a bounded, on-demand cost.
 * If the two ever need to share one request, lift the fetch to MapShell and pass data to both; the
 * shapes are identical. Kept apart here to hold the change to the stats panel (T-512's scope).
 *
 * Staleness is handled by AbortController, exactly as SiteVicinityLayer does it: searching B while
 * A is in flight aborts A, so a slow earlier response can never describe the new site.
 */
import { useEffect, useState } from 'react'
import type * as GeoJSON from 'geojson'
import { authedFetch } from '../lib/api'

export interface VicinityResponse {
  center: { lat: number; lng: number }
  radiusM: number
  blocks: GeoJSON.FeatureCollection
  blockGroups: GeoJSON.FeatureCollection
}

/** idle → no site to look up; loading → in flight; ready → `vicinity` is set; error → the fetch failed. */
export type VicinityStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface UseSiteVicinityResult {
  vicinity: VicinityResponse | null
  status: VicinityStatus
}

export function useSiteVicinity(site: { lat: number; lng: number } | null): UseSiteVicinityResult {
  const [vicinity, setVicinity] = useState<VicinityResponse | null>(null)
  const [status, setStatus] = useState<VicinityStatus>('idle')

  const lat = site?.lat ?? null
  const lng = site?.lng ?? null

  useEffect(() => {
    if (lat === null || lng === null) {
      setVicinity(null)
      setStatus('idle')
      return
    }

    const controller = new AbortController()
    setStatus('loading')

    const params = new URLSearchParams({ lat: String(lat), lng: String(lng) })
    authedFetch(`/api/vicinity?${params}`, { signal: controller.signal })
      .then(async (r) => {
        if (!r.ok) throw new Error(`vicinity ${r.status}`)
        return (await r.json()) as VicinityResponse
      })
      .then((data) => {
        if (controller.signal.aborted) return
        setVicinity(data)
        setStatus('ready')
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return
        setVicinity(null)
        setStatus('error')
      })

    return () => controller.abort()
  }, [lat, lng])

  return { vicinity, status }
}
